<script>
	import { onMount } from 'svelte';
	import { environment } from '../js/utils/environment';
	import { isPortraitScreen, pageOffset, universalScroll, toggleUniversalToPageScroll } from '../js/model/stores';

	import StartSection from '../sections/StartSection.svelte';
	import SupportersSection from "../sections/SupportersSection.svelte";
	import ParticipateSections from "../sections/ParticipateSections.svelte";
	import ShareSection from "../sections/ShareSection.svelte";
	import BottomSection from "../sections/BottomSection.svelte";
	import TopNavigation from "../components/TopNavigation.svelte";
	import BottomNavigation from "../components/BottomNavigation.svelte";

	export let name;


	// left: 37, up: 38, right: 39, down: 40,
	// spacebar: 32, pageup: 33, pagedown: 34, end: 35, home: 36
	const scrollKeys = {37: 1, 38: 1, 39: 1, 40: 1};

	// modern Chrome requires { passive: false } when adding event
	let supportsPassive = false;
	let wheelOpt;
	let wheelEvent;
	let lastTouchClientY;

	let _main;

	let lastUserEvent;

	/**
	 *
	 * @param {*} event Can be either scroll, touchmove, keydown
	 */
	let calculatePageOffset = () => {

		let event = lastUserEvent;
		let delta = 0;

		switch(event.type) {
			// Do nothing here but prevent the default case. We'll check the keyCode later
			case 'keydown':
				break;

			// All other events (scroll, wheel, mousewheel) should have the deltaY value
			default:
				delta = event.deltaY;
				break;
		}

		if (!event.keyCode) {
			let newOffset = Math.max(0, ($pageOffset + delta));

			// Stop scrolling down (positive delta)
			const mainBottomOffset = Math.floor(_main.getBoundingClientRect().bottom - environment().innerHeight());
			const footerHeight = document.querySelector('.bottom_navigation__content').getBoundingClientRect().height * 0.8;

			if (delta > 0 && (mainBottomOffset + footerHeight - delta) < 0) {
				return;
			}

			pageOffset.set(newOffset);
		}
		else if (scrollKeys[event.keyCode]) {

			switch (event.keyCode) {
				case 38:
					pageOffset.set($pageOffset - 10);
					break;
				case 40:
					pageOffset.set($pageOffset + 10);
					break;
			}
		}
	};

	let updatePageOffset = (event) => {

		lastUserEvent = event;

		// Universal scrolling is limited to non-touching users (mouse)
		if ($universalScroll && (!event.keyCode || scrollKeys[event.keyCode])) {
			event.preventDefault();
		}

		// Decelerate event frequency to what the browser is able to animate
		window.requestAnimationFrame(calculatePageOffset);
	};

	let initScrollingMonitor = () => {
		try {
			window.addEventListener("test", null, Object.defineProperty({}, 'passive', {
				get: function () { supportsPassive = true; }
			}));
		} catch(e) {}

		wheelOpt = supportsPassive ? { passive: false } : false;
		wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

		// If the page loads with an window offset (on page reload), it must have been scrolled behind the universal scroll section.
		const windowOffset = window.scrollY || window.pageYOffset;
		const initialPageOffset = windowOffset > 0 ? windowOffset + $toggleUniversalToPageScroll : 0;

		pageOffset.set(initialPageOffset);
		addUserInteractionListeners();
	};

	let monitorPageOffset = () => {
		pageOffset.set(window.scrollY || window.pageYOffset);
		window.requestAnimationFrame(monitorPageOffset);
	};

	let disableUniversalScrolling = () => {

		console.log('Disable universal scroll and just monitor the window offsetY.');

		removeUserInteractionListeners();
		window.removeEventListener('devicemotion', disableUniversalScrolling, false);

		toggleUniversalToPageScroll.set(0.1);
		universalScroll.set(false);

		monitorPageOffset();
	};

	let addUserInteractionListeners = () => {
		window.addEventListener('DOMMouseScroll', updatePageOffset, false); // older FF
		window.addEventListener(wheelEvent, updatePageOffset, wheelOpt); // modern desktop
		window.addEventListener('keydown', updatePageOffset, false);

		window.addEventListener('touchstart', disableUniversalScrolling, wheelOpt); // mobile
		window.addEventListener('touchend', disableUniversalScrolling, wheelOpt); // mobile
		window.addEventListener('touchmove', disableUniversalScrolling, wheelOpt); // mobile

		// Should we enable the Prallax effect?
		// On iOS we have to request permission (works on https only!)
		if (window.DeviceMotionEvent) {
			if (typeof window.DeviceMotionEvent.requestPermission === 'function') {
				window.DeviceMotionEvent.requestPermission()
					.then(response => {
						if (response === 'granted') {
							window.addEventListener('devicemotion', disableUniversalScrolling, false);
						}
					})
					.catch(console.error);
			}
		}
	};

	let removeUserInteractionListeners = () => {
		window.removeEventListener('DOMMouseScroll', updatePageOffset, false); // older FF
		window.removeEventListener(wheelEvent, updatePageOffset, wheelOpt); // modern desktop
		window.removeEventListener('keydown', updatePageOffset, false);

		window.removeEventListener('touchstart', disableUniversalScrolling, wheelOpt); // mobile
		window.removeEventListener('touchend', disableUniversalScrolling, wheelOpt); // mobile
		window.removeEventListener('touchmove', disableUniversalScrolling, wheelOpt); // mobile
	};

	let calculateCorrectViewportHeight = () => {
		// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
		let vhInPixel = window.innerHeight * 0.01;

		// Then we set the value in the --vh custom property to the root of the document
		document.documentElement.style.setProperty('--vh', `${vhInPixel}px`);
	};

	let setBaseFontSizeByScreenWidth = () => {

		const BASE_SIZE = 1.2;	// used as the default 'vw' and with calculations

		const vwInPixel = window.innerWidth * 0.01;
		const baseSizeInPixel = vwInPixel * BASE_SIZE;

		if (baseSizeInPixel < 16) {
			document.documentElement.style.setProperty('--base-size', '16px');
		}
		else if (baseSizeInPixel > 26) {
			document.documentElement.style.setProperty('--base-size', '26px');
		}
		else {
			document.documentElement.style.setProperty('--base-size', `${BASE_SIZE}vw`);
		}
	};

	let setScreenAspectRatio = () => {
		if (window.innerWidth < 640) {
			isPortraitScreen.set(window.innerHeight > window.innerWidth);
			document.body.classList[$isPortraitScreen ? 'add' : 'remove']('portrait');
		}
		else {
			isPortraitScreen.set(false);
			document.body.classList.remove('portrait');
		}
	};

	let updateScreenProperties = () => {
		calculateCorrectViewportHeight();
		setBaseFontSizeByScreenWidth();
		setScreenAspectRatio();
	};

	onMount(() => {

		// onMount triggers before the stage has been rendered.
		// The initialPageOffset is 0, regardless of the true (visible) scroll offset
		setTimeout(initScrollingMonitor, 500);

		// We listen to the resize event
		window.addEventListener('resize', updateScreenProperties);
		updateScreenProperties();

		pageOffset.subscribe((value) => {

			if ($universalScroll) {
				scroll({
					top: Math.max(0, (value - $toggleUniversalToPageScroll)),
					behavior: 'auto'	// instantly update the scroll position. Using 'smooth' should be used when jumping between anchors only!
				});
			}
			// else the page will be scrolled natively
		});
	});
</script>

<style type="text/css">
	main {
		text-align: center;
		padding: 0;
		margin: 0 auto;
		user-select: none;
		-webkit-user-drag: none;
		-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
		/* NO horizontal scrolling */
		overflow-x: hidden;
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}
</style>

<TopNavigation route="home"/>
<main bind:this={_main}>
	<StartSection route="home"/>
	<SupportersSection/>
	<ParticipateSections/>
	<ShareSection/>
	<BottomSection/>
</main>
<BottomNavigation/>
